import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import Button from "../../components/Button/Button";
import Checkmark from "../../components/Checkmark/Checkmark";
import "./ProductPage.scss";
import dukeElderBook from "../../assets/products/duke-elder-book.png";
import { FaBook, FaCheckCircle, FaExternalLinkAlt } from "react-icons/fa";

interface DukeElderPageProps {
  product: {
    id: string;
    price: number;
    length: string;
    comingSoon: boolean;
  };
}

const DukeElderPage = ({ product }: DukeElderPageProps) => {
  return (
    <>
      <NavBar />
      <div id="ProductPage">
        {/* Hero Section */}
        <div className="product-hero-section">
          <div className="product-hero-background">
            <div className="product-hero-left"></div>
            <div 
              className="product-hero-image-bg"
              style={{
                backgroundImage: `url(${dukeElderBook})`,
                backgroundSize: "contain",
                backgroundPosition: "center"
              }}
            ></div>
          </div>

          <div className="product-hero-overlay">
            <div className="site-container">
              <div className="product-hero-content">
                <h1>Duke Elder Examination Resource</h1>
                <p className="product-description">
                  <strong>Ophthalmology in 1000 MCQs</strong>
                </p>
                <p className="product-description" style={{ marginTop: "10px", fontSize: "1rem" }}>
                  By Diya Baker, Niha Hussain, Nada Alfahad, Gabriel Bellamy Plaice, Joseph Abbott, and Thomas Jenyon
                </p>
                <p className="product-description" style={{ marginTop: "15px", fontSize: "0.95rem", fontStyle: "italic" }}>
                  A comprehensive MCQ-based revision resource designed to support preparation for the Duke Elder Ophthalmology Examination (UK).
                </p>
                <div style={{ marginTop: "25px" }}>
                  <a
                    href="https://www.amazon.co.uk/Ophthalmology-1000-MCQs-Diya-Baker/dp/3032145708"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-block",
                      padding: "12px 30px",
                      background: "linear-gradient(135deg, #0e4f99 0%, #181c95 100%)",
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "8px",
                      fontWeight: "bold",
                      transition: "all 0.3s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "black";
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "linear-gradient(135deg, #0e4f99 0%, #181c95 100%)";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                      <span>Buy Now for £{product.price}</span>
                      <FaExternalLinkAlt style={{ fontSize: "0.9rem" }} />
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Book Image - Hidden on Desktop */}
        <div className="duke-elder-mobile-image" style={{
          display: "none",
          width: "100%",
          maxWidth: "300px",
          margin: "0 auto 40px",
          padding: "0 20px"
        }}>
          <img 
            src={dukeElderBook} 
            alt="Duke Elder Examination Resource Book"
            style={{
              width: "100%",
              height: "auto",
              display: "block",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
            }}
          />
        </div>

        {/* Main Content */}
        <div className="product-sales-content">
          <div className="site-container">
            
            {/* Introduction Section */}
            <section className="sales-section intro-section">
              <p className="lead-text">
                This book focuses on high-quality multiple-choice questions that reflect the depth, style, and breadth of topics tested in the Duke Elder exam. It provides structured exposure to both core fundamentals and advanced ophthalmic concepts, making it an invaluable tool for candidates aiming to strengthen exam technique and clinical reasoning.
              </p>
              <p className="solution-text">
                Even for those not sitting the Duke Elder exam, this book serves as a valuable global revision resource covering essential ophthalmology topics.
              </p>
            </section>

            {/* Book Details and Who It's For Section - Side by Side */}
            <section className="sales-section duke-elder-details-section">
              <div className="duke-elder-two-column">
                <div className="duke-elder-column">
                  <div className="duke-elder-card">
                    <div className="duke-elder-section-header">
                      <FaBook className="duke-elder-inline-icon" />
                      <h2>Book Details</h2>
                    </div>
                    <div className="simple-list">
                      <div className="list-item">
                        <span className="bullet"></span>
                        <p><strong>Publisher:</strong> Springer Nature Switzerland AG</p>
                      </div>
                      <div className="list-item">
                        <span className="bullet"></span>
                        <p><strong>Publication Date:</strong> April 2026</p>
                      </div>
                      <div className="list-item">
                        <span className="bullet"></span>
                        <p><strong>ISBN-13:</strong> 978-3032145703</p>
                      </div>
                      <div className="list-item">
                        <span className="bullet"></span>
                        <p><strong>ISBN-10:</strong> 3032145708</p>
                      </div>
                      <div className="list-item">
                        <span className="bullet"></span>
                        <p><strong>Format:</strong> Hardcover</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="duke-elder-column">
                  <div className="duke-elder-card">
                    <div className="duke-elder-section-header">
                      <FaBook className="duke-elder-inline-icon" />
                      <h2>Who It's For</h2>
                    </div>
                    <div className="simple-list">
                      <div className="list-item">
                        <FaCheckCircle style={{ color: "#10b981", fontSize: "1.2rem", flexShrink: 0 }} />
                        <p>Candidates preparing for the Duke Elder Ophthalmology Examination</p>
                      </div>
                      <div className="list-item">
                        <FaCheckCircle style={{ color: "#10b981", fontSize: "1.2rem", flexShrink: 0 }} />
                        <p>Medical students and junior doctors revising ophthalmology</p>
                      </div>
                      <div className="list-item">
                        <FaCheckCircle style={{ color: "#10b981", fontSize: "1.2rem", flexShrink: 0 }} />
                        <p>Ophthalmology trainees seeking structured MCQ-based revision</p>
                      </div>
                      <div className="list-item">
                        <FaCheckCircle style={{ color: "#10b981", fontSize: "1.2rem", flexShrink: 0 }} />
                        <p>Clinicians looking to refresh core and advanced ophthalmic knowledge</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* What This Book Covers Section */}
            <section className="sales-section highlight-section">
              <div className="highlight-header">
                <div className="highlight-badge">
                  <FaBook className="star-icon" />
                  <span>Comprehensive Coverage</span>
                </div>
                <h2>What This Book Covers</h2>
              </div>
              <div className="highlight-features">
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Comprehensive Coverage</h3>
                    <p>Ophthalmic anatomy, physiology, and pathology across all major topics</p>
                  </div>
                </div>
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Clinical Decision-Making</h3>
                    <p>Diagnostic modalities, therapeutic interventions, and surgical principles</p>
                  </div>
                </div>
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Exam-Aligned Questions</h3>
                    <p>1,000 MCQs aligned with Duke Elder standards with detailed explanations</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Where to Buy Section */}
            <section className="sales-section pricing-section">
              <div className="pricing-card">
                <h3>Where to Buy</h3>
                <p style={{ marginBottom: "20px", color: "#666" }}>
                  This book is available from the following retailers:
                </p>
                <div className="buy-links">
                  <a 
                    href="https://www.amazon.co.uk/Ophthalmology-1000-MCQs-Diya-Baker/dp/3032145708" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="buy-link"
                  >
                    <FaExternalLinkAlt style={{ marginRight: "8px" }} />
                    Buy on Amazon (UK)
                  </a>
                  <a 
                    href="https://www.amazon.sg/Ophthalmology-1000-MCQs-Diya-Baker/dp/3032145708" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="buy-link"
                  >
                    <FaExternalLinkAlt style={{ marginRight: "8px" }} />
                    Buy on Amazon (SG)
                  </a>
                  <a 
                    href="https://www.waterstones.com/book/ophthalmology-in-1000-mcqs/diya-baker/niha-hussain/9783032145703" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="buy-link"
                  >
                    <FaExternalLinkAlt style={{ marginRight: "8px" }} />
                    Buy on Waterstones
                  </a>
                  <a 
                    href="https://blackwells.co.uk/bookshop/product/Ophthalmology-in-1000-MCQs-by-Diya-Baker-Niha-Hussain-Nada-Alfahad-Gabriel-Bellamy-Plaice-Joseph-Abbott-Thomas-Jenyon/9783032145703" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="buy-link"
                  >
                    <FaExternalLinkAlt style={{ marginRight: "8px" }} />
                    Buy on Blackwell's
                  </a>
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DukeElderPage;

